import { useState } from "react";
import type { ApiClient } from "../api/client";
import { endpoints } from "../api/endpoints";
import { useKeyedWrite, useWrite } from "../api/hooks";
import type { CancellationResult, Money, PremisesOwner, Proposal, ProposalKind } from "../api/types";
import { FOUR_EYES, canStep, hasAnyRole, isLocal, type FourEyesKind } from "../auth/roles";
import { useFireContext } from "../context";
import { formatDateTime, shortId } from "../format/datetime";
import { showMoney } from "../format/money";
import { personLabel, Row, useConfigNames, type ConfigNames } from "../screens/shared";
import { StatusBadge } from "./Badges";
import { Dialog } from "./Dialog";
import {
  BILL_CANCELLATION_REASON,
  CANCELLATION_REASON,
  OWNER_KIND,
  PROPOSAL_KIND,
  PROPOSAL_STATUS,
  REVOCATION_REASON,
  labelOf,
  statusOf,
} from "./labels";
import { Notice, ProblemAlert, UnknownOutcome, UnknownUnkeyed } from "./ProblemAlert";
import { Button } from "@lcc/ui";

type Payload = Record<string, unknown>;
const payloadOf = (p: Proposal): Payload => (p.payload && typeof p.payload === "object" ? (p.payload as Payload) : {});
const str = (v: unknown): string => (typeof v === "string" ? v : "");
function money(v: unknown): Money | null {
  if (!v || typeof v !== "object") return null;
  const m = v as Money;
  return Number.isSafeInteger(m.amount) && m.currency === "ZMW" ? { amount: m.amount, currency: "ZMW" } : null;
}
function owner(v: unknown): PremisesOwner | null {
  if (!v || typeof v !== "object") return null;
  const o = v as PremisesOwner;
  return (o.kind === "person" || o.kind === "business") && typeof o.party_id === "string" ? { kind: o.kind, party_id: o.party_id } : null;
}
const ownerText = (o: PremisesOwner | null) => (o ? `${labelOf(OWNER_KIND, o.kind)} ${shortId(o.party_id)}` : "—");

/** Kinds whose endpoint takes no Idempotency-Key (`cancelIncident`); every other four-eyes endpoint is keyed. */
export const UNKEYED_KINDS: ReadonlySet<ProposalKind> = new Set(["incident_cancellation"]);

/**
 * The decision fields a confirmation repeats, taken from the proposal as shown (any difference is 409
 * `proposal_mismatch`). The contract's confirm variants: `incident_cancellation` repeats `reason_category` (and
 * `duplicate_of` when set), `risk_class_change` `risk_class`, `occupancy_change` `occupancy_type`, `owner_change`
 * `owner`, `bill_cancellation` `bill_source_id` and `reason_category`, `certificate_revocation` `reason_category`.
 */
export function confirmFieldsOf(p: Proposal): Payload {
  const d = payloadOf(p);
  switch (p.kind) {
    case "incident_cancellation":
      return str(d.duplicate_of)
        ? { reason_category: str(d.reason_category), duplicate_of: str(d.duplicate_of) }
        : { reason_category: str(d.reason_category) };
    case "risk_class_change":
      return { risk_class: str(d.risk_class) };
    case "occupancy_change":
      return { occupancy_type: str(d.occupancy_type) };
    case "owner_change":
      return { owner: owner(d.owner) };
    case "bill_cancellation":
      return { bill_source_id: str(d.bill_source_id), reason_category: str(d.reason_category) };
    case "certificate_revocation":
      return { reason_category: str(d.reason_category) };
    default:
      return {};
  }
}

/** Call the kind's four-eyes endpoint for this proposal's subject. */
export function submitProposalAction(api: ApiClient, p: Proposal, body: Payload, key: string): Promise<Proposal | CancellationResult> {
  const b = body as never;
  switch (p.kind) {
    case "incident_cancellation":
      return endpoints.cancelIncident(api, p.subject_id, b);
    case "risk_class_change":
      return endpoints.changeRiskClass(api, p.subject_id, b, key);
    case "occupancy_change":
      return endpoints.changeOccupancy(api, p.subject_id, b, key);
    case "owner_change":
      return endpoints.changeOwner(api, p.subject_id, b, key);
    case "bill_cancellation":
      return endpoints.cancelBill(api, p.subject_id, b, key);
    case "certificate_revocation":
      return endpoints.revokeCertificate(api, p.subject_id, b, key);
    default:
      return Promise.reject(new Error(`Unknown proposal kind ${String(p.kind)}`));
  }
}

/** The proposal inside a four-eyes answer (`cancelIncident` wraps it in a `CancellationResult`). */
export function proposalOf(r: Proposal | CancellationResult | undefined): Proposal | null {
  if (!r) return null;
  if ("result" in r) return r.result === "proposal" ? r.proposal : null;
  return r;
}

export type NextStep = { kind: "decided" } | { kind: "mine" } | { kind: "confirm" } | { kind: "waiting"; who: string } | { kind: "local" };

const WHO: Record<string, string> = {
  "fer:chief": "the chief",
  "fer:dispatcher": "another dispatcher",
  "fer:fire-officer": "a fire officer of the ward",
  "fer:admin": "a FER admin",
  "hub:finance-officer": "a finance officer",
};
export const whoCan = (roles: readonly string[]) => roles.map((r) => WHO[r] ?? r).join(" or ");

/**
 * What the signed-in person may do next with a proposal (UI only; the server enforces scope and conflict of
 * interest). The proposer is never offered confirm or reject, only withdraw; others need one of the kind's confirm
 * roles. A FER local session decides only incident cancellations.
 */
export function nextStep(p: Proposal, principal: { roles: string[]; partyId: string; auth?: "realm" | "local_auth" }): NextStep {
  if (p.status !== "pending") return { kind: "decided" };
  const rules = FOUR_EYES[p.kind as FourEyesKind];
  if (!rules) return { kind: "decided" };
  if (isLocal(principal) && p.kind !== "incident_cancellation") return { kind: "local" };
  if (p.proposed_by === principal.partyId) return { kind: "mine" };
  if (hasAnyRole(principal, rules.confirm)) return { kind: "confirm" };
  return { kind: "waiting", who: whoCan(rules.confirm) };
}

/** One line in words of what a proposal would do. */
export function proposalSummary(p: Proposal, names: ConfigNames): string {
  const d = payloadOf(p);
  switch (p.kind) {
    case "incident_cancellation":
      return `Cancel the incident (${labelOf(CANCELLATION_REASON, str(d.reason_category))})`;
    case "risk_class_change":
      return `Risk class ${names.riskClass(str(d.previous_risk_class))} → ${names.riskClass(str(d.risk_class))}`;
    case "occupancy_change":
      return `Occupancy ${names.occupancy(str(d.previous_occupancy_type))} → ${names.occupancy(str(d.occupancy_type))}`;
    case "owner_change":
      return `Owner ${ownerText(owner(d.previous_owner))} → ${ownerText(owner(d.owner))}`;
    case "bill_cancellation":
      return `Cancel the bill ${str(d.bill_source_id)} of ${showMoney(money(d.amount))} (${labelOf(BILL_CANCELLATION_REASON, str(d.reason_category))})`;
    case "certificate_revocation":
      return `Revoke the certificate (${labelOf(REVOCATION_REASON, str(d.reason_category))})`;
    default:
      return "—";
  }
}

/** The proposed values of a proposal, per kind, in words. */
export function ProposalPayload({ proposal }: { proposal: Proposal }) {
  const names = useConfigNames();
  const d = payloadOf(proposal);
  const rows: Array<[string, string]> = [];
  switch (proposal.kind) {
    case "incident_cancellation":
      rows.push(["Reason category", labelOf(CANCELLATION_REASON, str(d.reason_category))]);
      if (str(d.duplicate_of)) rows.push(["Duplicate of incident", shortId(str(d.duplicate_of))]);
      break;
    case "risk_class_change":
      rows.push(["Risk class now", names.riskClass(str(d.previous_risk_class))], ["Proposed risk class", names.riskClass(str(d.risk_class))]);
      break;
    case "occupancy_change":
      rows.push(["Occupancy now", names.occupancy(str(d.previous_occupancy_type))], ["Proposed occupancy", names.occupancy(str(d.occupancy_type))]);
      break;
    case "owner_change":
      rows.push(["Owner now", ownerText(owner(d.previous_owner))], ["Proposed owner", ownerText(owner(d.owner))]);
      break;
    case "bill_cancellation":
      rows.push(["Bill", str(d.bill_source_id)], ["Amount", showMoney(money(d.amount))], ["Reason category", labelOf(BILL_CANCELLATION_REASON, str(d.reason_category))]);
      break;
    case "certificate_revocation":
      rows.push(["Reason category", labelOf(REVOCATION_REASON, str(d.reason_category))]);
      if (str(d.inspection_id)) rows.push(["Inspection that found it", shortId(str(d.inspection_id))]);
      break;
  }
  rows.push(["Reason", str(d.reason) || "—"]);
  return (
    <dl className="fer-dl" aria-label="Proposed values">
      {rows.map(([t, v]) => (
        <Row key={t} term={t}>
          {v}
        </Row>
      ))}
    </dl>
  );
}

/** Who proposed, when, expiry and the decision. */
export function ProposalMeta({ proposal }: { proposal: Proposal }) {
  const { principal } = useFireContext();
  return (
    <dl className="fer-dl" aria-label="Proposal">
      <Row term="Kind">{labelOf(PROPOSAL_KIND, proposal.kind)}</Row>
      <Row term="Status">
        <StatusBadge status={statusOf(PROPOSAL_STATUS, proposal.status)} />
      </Row>
      <Row term="Proposed by">
        {personLabel(proposal.proposed_by, principal.partyId)} ({proposal.proposed_role}), {formatDateTime(proposal.proposed_at)}
      </Row>
      <Row term="Expires">{formatDateTime(proposal.expires_at)}</Row>
      {proposal.decided_at && (
        <Row term="Decided">
          {proposal.decided_by ? personLabel(proposal.decided_by, principal.partyId) : "the system"}
          {proposal.decided_role ? ` (${proposal.decided_role})` : ""}, {formatDateTime(proposal.decided_at)}
          {proposal.decision_reason ? ` – ${proposal.decision_reason}` : ""}
        </Row>
      )}
    </dl>
  );
}

/** The decision form of {@link ProposalDialog}, kept with an unknown-outcome lock. */
interface ProposalForm {
  mode: "confirm" | "reject" | "withdraw";
  note: string;
  reason: string;
}

/**
 * Review a pending proposal: confirm (repeating the proposed decision fields), reject (a reason), or, for the
 * proposer, withdraw. The proposer is never offered confirm or reject (403 `four_eyes_required`); each step is limited
 * to the kind's roles (403 `action_not_allowed`). A keyed kind locks after an unknown outcome and a reopened dialog
 * shows the locked decision read-only (TRN RW1).
 */
export function ProposalDialog({ proposal, onClose }: { proposal: Proposal; onClose: () => void }) {
  const { principal } = useFireContext();
  const names = useConfigNames();
  const step = nextStep(proposal, principal);
  const keyed = !UNKEYED_KINDS.has(proposal.kind);
  const withKey = useKeyedWrite((api, body: Payload, key) => submitProposalAction(api, proposal, body, key), `proposal:${proposal.proposal_id}`);
  const plain = useWrite((api, body: Payload) => submitProposalAction(api, proposal, body, ""));
  const locked = keyed && withKey.locked;
  const kept = keyed ? (withKey.lockedForm as ProposalForm | undefined) : undefined;
  const [mode, setMode] = useState<ProposalForm["mode"]>(kept?.mode ?? (step.kind === "mine" ? "withdraw" : "confirm"));
  const [note, setNote] = useState(kept?.note ?? "");
  const [reason, setReason] = useState(kept?.reason ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const repeated = confirmFieldsOf(proposal);
  const pending = keyed ? withKey.isPending : plain.isPending;
  const error = keyed ? withKey.error : plain.error;

  const onDone = (r: Proposal | CancellationResult) => {
    const p = proposalOf(r);
    setDone(p ? statusOf(PROPOSAL_STATUS, p.status).label.toLowerCase() : "confirmed");
  };
  const send = (body: Payload) => (keyed ? withKey.mutate(body, { onSuccess: onDone }) : plain.mutate(body, { onSuccess: onDone }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (locked) return send({});
    let body: Payload;
    if (mode === "confirm") {
      if (Object.values(repeated).some((v) => v === null || v === "")) return setErr("The proposal is missing a decision value; it cannot be confirmed here.");
      body = { action: "confirm", proposal_id: proposal.proposal_id, ...repeated };
      if (note.trim()) body.note = note.trim();
    } else {
      if (reason.trim().length < 3) return setErr("A reason of at least 3 characters is required.");
      body = { action: mode, proposal_id: proposal.proposal_id, reason: reason.trim() };
    }
    setErr(null);
    if (keyed) withKey.remember({ mode, note, reason } satisfies ProposalForm);
    send(body);
  };

  return (
    <Dialog title={`${labelOf(PROPOSAL_KIND, proposal.kind)}: review`} onClose={onClose} focusContainer={locked}>
      <div className="fer-form fer-approve">
        <div className="fer-approve__what">
          <h3>What will happen</h3>
          <p>{proposalSummary(proposal, names)}</p>
          <span className="fer-approve__rule">
            Proposed by {personLabel(proposal.proposed_by, principal.partyId)} · {formatDateTime(proposal.proposed_at)}. You can&apos;t approve your own
            proposal: a second person confirms it.
          </span>
        </div>
        <ProposalPayload proposal={proposal} />
        <ProposalMeta proposal={proposal} />
        {done ? (
          <>
            <p role="status" className="fer-success">
              <span aria-hidden="true">✓ </span>
              Done: the proposal is now {done}.
            </p>
            <div className="fer-actions">
              <Button onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        ) : step.kind === "decided" ? (
          <Notice>This proposal has been decided; nothing more to do.</Notice>
        ) : step.kind === "local" ? (
          <Notice tone="warning">With the FER local sign-in only incident cancellations can be decided. Sign in normally to decide this.</Notice>
        ) : step.kind === "waiting" ? (
          <Notice>Waiting for {step.who}. You cannot take this step.</Notice>
        ) : (
          <form className="fer-form" onSubmit={submit} noValidate>
            {step.kind === "mine" ? (
              <Notice tone="warning">You proposed this, so a different person must confirm it. You may withdraw it.</Notice>
            ) : (
              <fieldset className="fer-fieldset" disabled={locked}>
                <legend>Your decision</legend>
                <div className="fer-segmented">
                  <label className="fer-check">
                    <input type="radio" name="decision" checked={mode === "confirm"} onChange={() => setMode("confirm")} />
                    Confirm
                  </label>
                  <label className="fer-check">
                    <input type="radio" name="decision" checked={mode === "reject"} onChange={() => setMode("reject")} />
                    Reject
                  </label>
                </div>
              </fieldset>
            )}
            {mode === "confirm" && step.kind === "confirm" && (
              <>
                <p className="fer-muted">
                  Confirming sends the proposed values above back unchanged; if the proposal or its subject changed meanwhile,
                  the confirmation is refused and nothing happens.
                </p>
                <label className="fer-field">
                  <span>Note (optional)</span>
                  <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} disabled={locked} maxLength={1000} />
                </label>
              </>
            )}
            {mode !== "confirm" && (
              <label className="fer-field">
                <span>{mode === "withdraw" ? "Reason for withdrawing (required)" : "Reason for rejecting (required)"}</span>
                <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={locked} maxLength={1000} />
              </label>
            )}
            {err && (
              <p className="fer-form-error" role="alert">
                {err}
              </p>
            )}
            <ProblemAlert error={error} />
            {keyed ? <UnknownOutcome write={withKey} retry={() => send({})} /> : <UnknownUnkeyed show={plain.unknown} />}
            <div className="fer-actions">
              <Button variant="secondary" onClick={onClose}>
                Back
              </Button>
              <Button type="submit" disabled={pending}>
                {mode === "confirm" ? "Confirm" : mode === "reject" ? "Reject" : "Withdraw proposal"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Dialog>
  );
}

/** After a successful propose: what happens next. */
export function ProposedResult({ proposal }: { proposal: Proposal }) {
  const rules = FOUR_EYES[proposal.kind as FourEyesKind];
  return (
    <p role="status" className="fer-success">
      <span aria-hidden="true">✓ </span>
      Proposed. It now waits for {rules ? whoCan(rules.confirm) : "a second person"} other than you (expires {formatDateTime(proposal.expires_at)}).
    </p>
  );
}

/** Whether the principal may propose this kind (UI only). */
export function canPropose(principal: { roles: string[]; auth?: "realm" | "local_auth" }, kind: ProposalKind): boolean {
  return canStep(principal, kind as FourEyesKind, "propose");
}

/** A pending proposal on a record, as a line with a review button. */
export function PendingProposalLine({ proposal, onOpen }: { proposal: Proposal; onOpen: () => void }) {
  const { principal } = useFireContext();
  const names = useConfigNames();
  const step = nextStep(proposal, principal);
  return (
    <p className="fer-pending">
      <StatusBadge status={statusOf(PROPOSAL_STATUS, proposal.status)} /> {proposalSummary(proposal, names)}, proposed by{" "}
      {personLabel(proposal.proposed_by, principal.partyId)} on {formatDateTime(proposal.proposed_at)}{" "}
      <Button variant="secondary" size="sm" onClick={onOpen}>
        {step.kind === "confirm" ? "Review and decide" : step.kind === "mine" ? "View or withdraw" : "View"}
      </Button>
    </p>
  );
}
